import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Funktionseinheit, LueftungAnlagenDetail, LueftungFeld } from '../../api';
import { EinheitText, IconEinheit } from '../../EinheitIcons';
import { Legende } from '../../Farbregel';
import {
  ContentSection, DataTable, Drawer, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState, PropertyGrid,
  StatusBadge, Vorbehalte, formatiereZahl, type Kennwert, type Spalte, type Vorbehalt,
} from '../../muster';
import { datenblattStatus, inArbeit, nurGeaenderte, werteAus } from './bestandregeln';
import { Ablagefeld, Unterlage } from './LueftungDatenblatt';
import { Feldgruppen } from './LueftungFeld';
import { tag } from './pruefung';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EINE LÜFTUNGSANLAGE (TV6c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Anlage ist das führende Objekt des Bestands: Modellwerte, Revit-
 * Elemente, Gerätedatenblatt, Funktionseinheiten, Kanalsysteme und die Felder
 * für Leistung, Aufbau und Betrieb, die es nur in der Datenbank gibt.
 *
 * Schreibwege wie vor TV6c (Charakterisierung `Lueftung.schreibwege.test.tsx`):
 * `lueftungAnlageSpeichern(anlage, nur die geänderten Felder als Text)` (TVX3; bis dahin ALLE Werte), `lueftungAnlagenlistePdf`,
 * Gerätedatenblatt über `datenblattFinden('Anlage ‹Name›', ‹Name›, –, ‹Id›)` +
 * `datenblattDatei` und `datenblattBeauftragen(…, 'pdf')`.
 */
export function Anlagenseite({ api, projektId, anlageId, zuBauteilen }: {
  api: ApiClient;
  projektId: string;
  anlageId: string;
  zuBauteilen: () => void;
}) {
  const [anlage, setAnlage] = useState<LueftungAnlagenDetail | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [herkunft, setHerkunft] = useState<Record<string, string>>({});
  const [geaendert, setGeaendert] = useState(false);
  const [geaenderteFelder, setGeaenderteFelder] = useState<string[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [gesichert, setGesichert] = useState(false);

  const laden = useCallback(() => {
    setAnlage(null);
    setGeaendert(false);
    setGeaenderteFelder([]);
    setGesichert(false);
    setFehler(null);
    setLadefehler(null);
    api.lueftungAnlage(anlageId)
      .then((a) => {
        setAnlage(a);
        setWerte(werteAus(a.dataJson));
        setHerkunft(werteAus(a.herkunft));
      })
      .catch(setLadefehler);
  }, [api, anlageId]);

  useEffect(() => { laden(); }, [laden]);

  const sichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.lueftungAnlageSpeichern(anlageId, nurGeaenderte(werte, geaenderteFelder));
      setGeaendert(false);
      setGeaenderteFelder([]);
      setGesichert(true);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const elementspalten = useMemo<Spalte<LueftungAnlagenDetail['elemente'][number]>[]>(() => [
    { schluessel: 'rolle', titel: 'Rolle', wert: (e) => e.rolle, immerSichtbar: true },
    { schluessel: 'familie', titel: 'Familie', wert: (e) => e.familie ?? '' },
    { schluessel: 'typ', titel: 'Typ', wert: (e) => e.typ ?? '' },
    { schluessel: 'ebene', titel: 'Ebene', wert: (e) => e.ebene ?? '' },
    {
      schluessel: 'lage', titel: 'Lage', einheit: 'mm',
      wert: (e) => (e.posX !== null ? `${formatiereZahl(e.posX, 0)} / ${formatiereZahl(e.posY, 0)} / ${formatiereZahl(e.posZ, 0)}` : ''),
    },
  ], []);

  const systemspalten = useMemo<Spalte<LueftungAnlagenDetail['systeme'][number]>[]>(() => [
    { schluessel: 'luftart', titel: 'Luftart', wert: (s) => s.luftart ?? s.abkuerzung ?? '', immerSichtbar: true },
    { schluessel: 'system', titel: 'System', wert: (s) => s.systemname },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (s) => s.bauteile },
    {
      schluessel: 'art', titel: 'Art', art: 'status',
      wert: (s) => (s.istVerbundnetz ? `Verbund ${s.anlagenAnzahl}` : 'eigen'),
      inhalt: (s) => (s.istVerbundnetz
        ? <StatusBadge zustand="neutral" text={`Verbundnetz von ${formatiereZahl(s.anlagenAnzahl, 0)} Anlagen`} klein />
        : 'eigen'),
    },
  ], []);

  if (ladefehler !== null) {
    return (
      <ContentSection titel="Anlage">
        <ErrorState meldung="Die Anlage konnte nicht abgerufen werden." technisch={ladefehler} beiErneutVersuchen={laden} />
      </ContentSection>
    );
  }
  if (!anlage) {
    return <ContentSection titel="Anlage"><LoadingState text="Anlage wird abgerufen …" zeilen={6} /></ContentSection>;
  }

  const bauteile = anlage.systeme.reduce((s, x) => s + x.bauteile, 0);
  const vorbehalte: Vorbehalt[] = anlage.ebenenkonflikt ? [{
    art: 'sonstiges',
    text: `Das Geschoss im Namen (${anlage.aksGeschoss ?? '—'}) weicht von der Revit-Ebene ab (${anlage.aksGeschossModell ?? '—'}). `
      + `Gültig ist der Name - die Ebene ${anlage.ebene ?? ''} trägt im Modell mehrere Geschosse und kann sie nicht unterscheiden.`,
  }] : [];

  return (
    <>
      <ContentSection
        titel={anlage.anlage}
        beschreibung={anlage.aksSchluessel ?? undefined}
        aktionen={(
          <>
            {/* Derselbe Abschnitt, den die Anlagenliste als PDF ausgibt - hier für die gewählte Anlage. */}
            <ExportLeiste ziele={[{
              art: 'pdf', beschriftung: 'Anlagendetails als PDF',
              hinweis: 'Stammdaten, Geräteaufbau, Register, KVS, Kanalnetz und versorgte Räume',
              laden: () => api.lueftungAnlagenlistePdf(projektId, anlage.anlage),
            }]}
            />
            <button type="button" className="m-knopf" onClick={zuBauteilen}>{formatiereZahl(bauteile, 0)} Bauteil(e) →</button>
          </>
        )}
      >
        <Vorbehalte eintraege={vorbehalte} />
        {/* Was aus dem Modell kam. Unveränderlich, weil es dort gepflegt wird. */}
        <PropertyGrid
          spalten={3}
          dicht
          eintraege={[
            { beschriftung: 'Geschoss', wert: anlage.ebene ?? '—', herkunft: 'modell' },
            { beschriftung: 'AKS-Geschoss', wert: anlage.aksGeschoss ?? '—', herkunft: 'modell' },
            { beschriftung: 'Anlagengruppe', wert: anlage.aksGruppe ?? '—', herkunft: 'modell' },
            { beschriftung: 'Laufnummer', wert: anlage.aksNummer, nachkomma: 0, herkunft: 'modell' },
            {
              beschriftung: 'Raum', herkunft: 'modell',
              wert: anlage.raumnummer ? `${anlage.raumnummer}${anlage.raumname ? ` · ${anlage.raumname}` : ''}` : '—',
            },
            {
              beschriftung: 'Bezeichnung aus', herkunft: 'modell',
              wert: anlage.anlageQuelle === 'parameter' ? 'Parameter SI_TE_DM_ElementID'
                : anlage.anlageQuelle === 'system' ? 'Kanalsystem abgeleitet' : 'von Hand',
            },
          ]}
        />
      </ContentSection>

      {/* Die Revit-Elemente. Zwölf Anlagen bestehen aus zwei. */}
      <ContentSection titel="Revit-Elemente">
        <DataTable
          name="Revit-Elemente"
          spalten={elementspalten}
          zeilen={anlage.elemente}
          schluessel={(e) => e.id}
          suche={false}
          dicht
          leer={<span>Keine Revit-Elemente.</span>}
        />
      </ContentSection>

      <Geraetedatenblatt api={api} anlage={anlage} neuLaden={() => { void api.lueftungAnlage(anlageId).then(setAnlage); }} />

      {anlage.einheiten.length > 0 && <Einheitenfolge anlage={anlage} />}

      {/* Die Kanalsysteme. Ein Verbundnetz gehört mehreren Anlagen. */}
      <ContentSection titel="Kanalsysteme">
        <DataTable
          name="Kanalsysteme"
          spalten={systemspalten}
          zeilen={anlage.systeme}
          schluessel={(s) => s.id}
          suche={false}
          dicht
          leer={<span>Keine Kanalsysteme.</span>}
        />
      </ContentSection>

      <ContentSection titel="Werte pflegen" beschreibung="Leistung, Aufbau und Betrieb der Anlage - nur in der Datenbank, je Feld mit Rückschreibweg.">
        <Legende />
        <Feldgruppen
          felder={anlage.felder}
          werte={werte}
          herkunft={herkunft}
          setzen={(k, v) => {
            setWerte((w) => ({ ...w, [k]: v }));
            setGeaenderteFelder((g) => (g.includes(k) ? g : [...g, k]));
            setGeaendert(true);
            setGesichert(false);
          }}
        />
        {fehler && <ErrorState klein meldung={fehler} />}
        <div className="lue-speichern">
          <button type="button" className="m-knopf m-knopf-haupt" disabled={!geaendert || laeuft} onClick={() => void sichern()}>
            Speichern
          </button>
          <span className="lue-meldung" role="status">{gesichert ? 'gespeichert' : ''}</span>
          {anlage.updatedBy && <span className="lue-leise">zuletzt: {anlage.updatedBy}</span>}
        </div>
      </ContentSection>
    </>
  );
}

// ═══ Gerätedatenblatt ═══════════════════════════════════════════════════════════
//
// Anders als bei den Bauteilen gehört es zu DIESER Anlage: Ein robatherm-Blatt
// beschreibt dieses Gerät dieses Projekts, mit Angebotsnummer und Auslegung.

function Geraetedatenblatt({ api, anlage, neuLaden }: {
  api: ApiClient;
  anlage: LueftungAnlagenDetail;
  neuLaden: () => void;
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const blatt = anlage.datenblatt;
  const wird = inArbeit(blatt?.zustand);

  // Solange ausgewertet wird, nachladen - ein 17-seitiges Gerätedatenblatt braucht ein bis drei Minuten.
  useEffect(() => {
    if (!wird) return undefined;
    const takt = setInterval(neuLaden, 20_000);
    return () => clearInterval(takt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wird]);

  const hochladen = async (datei: File) => {
    setLaeuft('hochladen');
    setFehler(null);
    setMeldung(null);
    try {
      // Datensatz zur Anlage holen oder anlegen, dann die Datei.
      const b = await api.datenblattFinden(`Anlage ${anlage.anlage}`, anlage.anlage, undefined, anlage.id);
      await api.datenblattDatei(b.id, datei);
      setMeldung(`${datei.name} abgelegt. Jetzt „Datenblatt auswerten“ beauftragen.`);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const auswerten = async () => {
    if (!blatt) return;
    setLaeuft('auswerten');
    setFehler(null);
    setMeldung(null);
    try {
      await api.datenblattBeauftragen(blatt.id, 'pdf');
      setMeldung('In die Warteschlange gestellt. Die Funktionseinheiten erscheinen hier, sobald die Auswertung fertig ist.');
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const angaben: Kennwert[] = blatt ? [
    { beschriftung: 'Hersteller', wert: blatt.hersteller ?? '—', herkunft: 'daten' },
    { beschriftung: 'Gerätebezeichnung', wert: blatt.geraetebezeichnung ?? '—', herkunft: 'daten' },
    { beschriftung: 'Angebotsnummer', wert: blatt.angebotsnummer ?? '—', herkunft: 'daten' },
    { beschriftung: 'Angebotsdatum', wert: blatt.datenblattDatum ?? '—', herkunft: 'daten' },
    // Fassung und Übernahme: Ohne sie ist nicht erkennbar, ob die Werte von heute oder von vor drei Monaten sind.
    ...(blatt.aktuelleDatenversion
      ? [{ beschriftung: 'Geräteversion', wert: blatt.aktuelleDatenversion + (blatt.fassungen > 1 ? ` von ${blatt.fassungen}` : '') }]
      : []),
    ...(blatt.importiertAm
      ? [{ beschriftung: 'Übernommen', wert: `${tag(blatt.importiertAm)}${blatt.importiertVon ? ` von ${blatt.importiertVon}` : ''}` }]
      : []),
  ] : [];

  // Der Hinweis trägt die Druckverlustprobe. Weicht sie ab, steht dort „ACHTUNG“ - das soll man sehen, nicht suchen.
  const achtung = !!blatt?.zustandHinweis?.includes('ACHTUNG');

  return (
    <ContentSection
      titel="Gerätedatenblatt"
      meta={blatt ? <StatusBadge {...datenblattStatus(blatt.zustand)} /> : undefined}
      beschreibung={blatt ? undefined
        : 'Zu dieser Anlage liegt kein Gerätedatenblatt. Es beschreibt das Gerät dieses Projekts - Gehäuseklassen, Auslegung und die Folge der Funktionseinheiten.'}
    >
      {blatt && (
        <>
          {achtung && !meldung && <Vorbehalte eintraege={[{ art: 'sonstiges', text: blatt.zustandHinweis ?? '' }]} />}
          <PropertyGrid spalten={3} dicht eintraege={angaben} />
          {(blatt.hatDatei || blatt.hatSkizze) && (
            <div className="lue-unterlagen">
              <Unterlage titel="Technisches Datenblatt" dateiname={blatt.dateiname} groesse={blatt.dateiGroesse} vorhanden={blatt.hatDatei} verweis={anlage.datenblattUrl} />
              <Unterlage titel="Geräteskizze" dateiname={blatt.skizzeDateiname} groesse={blatt.skizzeGroesse} vorhanden={blatt.hatSkizze} verweis={anlage.skizzeUrl} />
            </div>
          )}
        </>
      )}

      <Ablagefeld
        beschriftung={blatt?.hatDatei ? 'Anderes Datenblatt hierher ziehen oder klicken' : 'Gerätedatenblatt hierher ziehen oder klicken'}
        laeuft={laeuft === 'hochladen'}
        ablegen={(d) => { void hochladen(d); }}
        fehlerMelden={setFehler}
      />

      <div className="lue-speichern">
        <button
          type="button"
          className="m-knopf m-knopf-haupt"
          disabled={laeuft !== null || wird || !blatt?.hatDatei}
          onClick={() => void auswerten()}
        >
          Datenblatt auswerten
        </button>
        <span className="lue-leise">Ein Blatt mit 17 Seiten braucht ein bis drei Minuten.</span>
      </div>

      {fehler && <ErrorState klein meldung={fehler} />}
      <p className="lue-meldung" role="status">{meldung ?? (!achtung && blatt?.zustandHinweis ? blatt.zustandHinweis : '')}</p>
    </ContentSection>
  );
}

// ═══ Funktionseinheiten und Druckbilanz ══════════════════════════════════════════
//
// Ein Lüftungsgerät ist eine Reihe in Durchströmungsrichtung: Anschlussrahmen,
// Klappe, Filter, Schalldämpfer, Erhitzer, …, Ventilator. Unten die
// Druckverlustprobe: Stimmt die Summe ohne Ventilator mit der Herstellerangabe
// (RLT BOWL ZUL: 651 Pa, Datenblatt S. 11), ist keine Einheit übersehen.

const LUFTART_TEXT: Record<string, string> = { ZU: 'Zuluft', AB: 'Abluft', AU: 'Außenluft', FO: 'Fortluft' };

function Einheitenfolge({ anlage }: { anlage: LueftungAnlagenDetail }) {
  const [offen, setOffen] = useState<Funktionseinheit | null>(null);
  const stroeme = [...new Set(anlage.einheiten.map((e) => e.luftstrom))];

  const spalten: Spalte<Funktionseinheit>[] = [
    { schluessel: 'nr', titel: 'Nr', art: 'zahl', nachkomma: 0, wert: (e) => e.reihenfolge },
    {
      schluessel: 'art', titel: 'Art', wert: (e) => EinheitText[e.art] ?? e.art, immerSichtbar: true,
      // Das Sinnbild macht die Folge lesbar: Zwanzig Einheiten erkennt man am Bild, nicht am Wort.
      inhalt: (e) => <span className="lue-einheit"><IconEinheit art={e.art} size={24} title={EinheitText[e.art] ?? e.art} /> {EinheitText[e.art] ?? e.art}</span>,
    },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (e) => e.bezeichnung },
    {
      schluessel: 'dp', titel: 'Δp', art: 'zahl', einheit: 'Pa', nachkomma: 0, wert: (e) => e.druckverlustPa,
      inhalt: (e) => (e.art === 'VENT' && e.druckverlustPa ? `+${formatiereZahl(e.druckverlustPa, 0)}` : formatiereZahl(e.druckverlustPa, 0)),
    },
    {
      schluessel: 'angaben', titel: 'Angaben', art: 'zahl', nachkomma: 0,
      wert: (e) => Object.keys(werteAus(e.dataJson)).length + (e.betriebspunkte ?? []).length,
    },
  ];

  return (
    <ContentSection
      titel="Funktionseinheiten"
      beschreibung="Je Luftstrom in Durchströmungsrichtung. Beim Ventilator steht der gelieferte Druck (+), er verbraucht keinen. Eine Zeile wählen zeigt ihre Angaben und Betriebspunkte."
    >
      {stroeme.map((strom) => {
        const eigene = anlage.einheiten.filter((e) => e.luftstrom === strom).sort((a, b) => a.reihenfolge - b.reihenfolge);
        const probe = anlage.druck.find((d) => d.luftstrom === strom);
        return (
          <div key={strom} className="lue-strom">
            <DataTable<Funktionseinheit>
              name={`${LUFTART_TEXT[strom] ?? strom} · ${formatiereZahl(eigene.length, 0)} Einheiten`}
              spalten={spalten}
              zeilen={eigene}
              schluessel={(e) => e.id}
              sortierung={{ spalte: 'nr', richtung: 'auf' }}
              beiZeileKlick={setOffen}
              suche={false}
              dicht
            />
            {probe && (
              <KpiReihe>
                <KpiCard titel="Interner Druckverlust" wert={probe.internPa} einheit="Pa" nachkomma={0} vergleich="Summe der Einheiten ohne Ventilator" />
                {probe.ventilatorPa > 0 && (
                  <KpiCard titel="Ventilator, Gesamtdruck" wert={probe.ventilatorPa} einheit="Pa" nachkomma={0} vergleich="extern + intern + Einbauverlust" />
                )}
                {probe.ventilatorPa > probe.internPa && (
                  <KpiCard titel="Verbleibt extern" wert={probe.ventilatorPa - probe.internPa} einheit="Pa" nachkomma={0} vergleich="für Kanalnetz und Einbauteile (ungefähr)" />
                )}
              </KpiReihe>
            )}
          </div>
        );
      })}
      {offen && <Einheitdetail einheit={offen} felder={anlage.einheitsfelder} schliessen={() => setOffen(null)} />}
    </ContentSection>
  );
}

function Einheitdetail({ einheit, felder, schliessen }: {
  einheit: Funktionseinheit;
  felder: (LueftungFeld & { aksCodes: string | null })[];
  schliessen: () => void;
}) {
  const werte = werteAus(einheit.dataJson);
  // Nur die Felder, die für diese Art gelten - dieselbe Mechanik wie beim Bauteil.
  const gueltig = felder.filter((f) => !f.aksCodes || f.aksCodes.split(',').includes(einheit.art));
  const ohneFeld = Object.entries(werte).filter(([k]) => !gueltig.some((f) => f.fieldKey === k));
  const punkte = einheit.betriebspunkte ?? [];

  return (
    <Drawer offen titel={`${EinheitText[einheit.art] ?? einheit.art} · ${einheit.bezeichnung}`} breite="720px" beiSchliessen={schliessen}
            fuss={<button type="button" className="m-knopf" onClick={schliessen}>schließen</button>}>
      <PropertyGrid
        spalten={2}
        dicht
        eintraege={gueltig.filter((f) => werte[f.fieldKey]).map((f) => ({
          beschriftung: f.unit ? `${f.label} [${f.unit}]` : f.label, wert: werte[f.fieldKey],
        }))}
      />
      {punkte.length > 0 && (
        <DataTable
          name="Betriebspunkte"
          spalten={[
            { schluessel: 'modus', titel: 'Betriebspunkt', wert: (p) => p.modusDe ?? p.modus ?? `Punkt ${p.reihenfolge}`, immerSichtbar: true },
            { schluessel: 'leistung', titel: 'Leistung', art: 'zahl', einheit: 'kW', nachkomma: 1, wert: (p) => zahlAus(p.daten, 'bp_leistung') },
            { schluessel: 'sens', titel: 'sensibel', art: 'zahl', einheit: 'kW', nachkomma: 1, wert: (p) => zahlAus(p.daten, 'bp_leistung_sens') },
            { schluessel: 'lat', titel: 'latent', art: 'zahl', einheit: 'kW', nachkomma: 1, wert: (p) => zahlAus(p.daten, 'bp_leistung_lat') },
            {
              schluessel: 'netz', titel: 'Netztemperaturen', einheit: '°C',
              wert: (p) => { const d = werteAus(p.daten); return d.bp_medium_vl && d.bp_medium_rl ? `${d.bp_medium_vl} / ${d.bp_medium_rl}` : ''; },
            },
            { schluessel: 'massenstrom', titel: 'Massenstrom', art: 'zahl', einheit: 'kg/h', nachkomma: 0, wert: (p) => zahlAus(p.daten, 'bp_massenstrom') },
            { schluessel: 'rohrnetz', titel: 'Rohrnetz', wert: (p) => werteAus(p.daten).bp_net_gruppe ?? '' },
          ]}
          zeilen={punkte}
          schluessel={(p) => p.id}
          suche={false}
          dicht
        />
      )}
      {/* Werte, für die es kein Feld gibt: nicht verschweigen - sonst geht bei der Auswertung etwas verloren. */}
      {ohneFeld.length > 0 && (
        <p className="lue-leise">Ohne Feld im Katalog: {ohneFeld.map(([k, v]) => `${k}=${v}`).join(', ')}</p>
      )}
    </Drawer>
  );
}

/** Eine Zahl aus den Daten eines Betriebspunkts (gespeichert als Text, Punkt als Dezimaltrenner), sonst Fehlstelle. */
function zahlAus(daten: string | null, schluessel: string): number | null {
  const roh = werteAus(daten)[schluessel];
  const zahl = roh === undefined || roh === '' ? Number.NaN : Number(roh);
  return Number.isFinite(zahl) ? zahl : null;
}
